import { useState, useCallback, useEffect, useMemo, type MutableRefObject } from 'react';
import { MINIMUM_ZOOM_LEVEL, DEFAULT_ZOOM_LEVEL } from './ellipsis.tsx';
import type { ContentBounds } from './clampPan.tsx';
import useResizeObserver from './useResizeObserver.tsx';
import { calculateFitZoom } from './getVerticalOffset.tsx';

type ContainerSize = {
	width: number;
	height: number;
};

export const useAutoFitZoom = (
	contentBounds: ContentBounds | undefined,
	canvasContainerRef: MutableRefObject<HTMLDivElement | null>,
	isLayoutStable: boolean,
	fallbackMinimum: number = MINIMUM_ZOOM_LEVEL,
	enabled = true,
): number => {
	const [containerSize, setContainerSize] = useState<ContainerSize | undefined>();

	const handleResize = useCallback((entry: ResizeObserverEntry) => {
		const { width, height } = entry.contentRect;
		setContainerSize({ width, height });
	}, []);

	const { setRef: setResizeObserverRef } = useResizeObserver(handleResize);

	useEffect(() => {
		setResizeObserverRef(enabled ? canvasContainerRef.current : null);
	}, [setResizeObserverRef, canvasContainerRef, enabled]);

	const dynamicMinZoom = useMemo(() => {
		if (!enabled || !isLayoutStable || !contentBounds || !containerSize) {
			return fallbackMinimum;
		}

		const { width: containerWidth, height: containerHeight } = containerSize;

		if (containerWidth <= 0 || containerHeight <= 0) {
			return fallbackMinimum;
		}

		const fitZoom = calculateFitZoom(contentBounds, containerSize);

		return Math.min(fitZoom, fallbackMinimum, DEFAULT_ZOOM_LEVEL);
	}, [contentBounds, containerSize, isLayoutStable, fallbackMinimum, enabled]);

	return dynamicMinZoom;
};

export default useAutoFitZoom;
